import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Select,
  Progress,
  Tag,
  Button,
  Modal,
  Space,
  Avatar,
  Input,
} from "antd";
import {
  UserOutlined,
  CalendarOutlined,
  CommentOutlined,
  SendOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import { all_routes } from "../router/all_routes";
import CollapseHeader from "../../core/common/collapse-header/collapse-header";
import moment from "moment";
import api from "../../services/api";
import { useSelector } from "react-redux";
import { showBasicAlert } from "../../core/common/useAlert";
import { getEmployeeImageUrl } from "../../services/assetCommonService";
import CommonSelect from "../../core/common/commonSelect";
import Datatable from "../../core/common/dataTable";
import DeleteModal from "../../core/modals/deleteModal";

const { Option } = Select;
const { TextArea } = Input;

interface Comment {
  id: number;
  comment: string;
  createdAt: string;
  user_id: number;
  t_ticket_id: number;
}

interface TicketRecord {
  id: number;
  title: string;
  description: string;
  priority: string;
  status: string;
  progress: number;
  due_date: string;
  time_spent: string;
  raised_by: number;
  raised_date: string;
  category: string;
  is_overdue: boolean;
}

// Constants - defined outside component
const STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "in-progress", label: "In Progress" },
  { value: "pending", label: "Pending" },
  { value: "completed", label: "Completed" },
];

const PRIORITY_OPTIONS = [
  { value: "all", label: "All Priority" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

const AssignedTickets = () => {
  const routes = all_routes;
  const user = useSelector((state: any) => state.user.user);
  const signature = localStorage.getItem("signature") || "";

  // State for tickets
  const [assignedTickets, setAssignedTickets] = useState<TicketRecord[]>([]);
  const [metrics, setMetrics] = useState<any>({});
  const [loading, setLoading] = useState(true);
  const [ticketUpdating, setTicketUpdating] = useState<Record<number, boolean>>({});

  // Employee details state
  const [employeeDetail, setEmployeeDetail] = useState<any[]>([]);
  const [employeeNameCache, setEmployeeNameCache] = useState<Record<number, string>>({});
  const [employeeImageCache, setEmployeeImageCache] = useState<Record<number, any>>({});

  // Filter states
  const [selectedStatus, setSelectedStatus] = useState<string>("all");
  const [selectedPriority, setSelectedPriority] = useState<string>("all");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Company state
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [companyType, setCompanyType] = useState<string>("");
  const [companies, setCompanies] = useState<any[]>([]);

  // Comments modal state
  const [isCommentsModalVisible, setIsCommentsModalVisible] = useState(false);
  const [currentTicketId, setCurrentTicketId] = useState<number | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState("");
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editCommentText, setEditCommentText] = useState<string>("");
  const [commentCounts, setCommentCounts] = useState<Record<number, number>>({});
  const [commentsLoading, setCommentsLoading] = useState(false);

  const [openFullText, setOpenFullText] = useState(false);
  const [fullTextValue, setFullTextValue] = useState("");

  const [deleteCommentModal, setDeleteCommentModal] = useState<{
    visible: boolean;
    commentId: number | null;
  }>({
    visible: false,
    commentId: null
  });

  // Build employee caches
  useEffect(() => {
    if (employeeDetail.length > 0) {
      const nameCache: Record<number, string> = {};
      const imageCache: Record<number, any> = {};

      employeeDetail.forEach((emp: any) => {
        const empId = parseInt(emp.key);
        nameCache[empId] = emp.Name;
        imageCache[empId] = getEmployeeImageUrl(empId, employeeDetail, user);
      });

      setEmployeeNameCache(nameCache);
      setEmployeeImageCache(imageCache);
    }
  }, [employeeDetail, user]);

  // Bulk comment count fetch
  const fetchBulkCommentCounts = useCallback(async (ticketIds: number[]) => {
    try {
      const response = await api.post("raiseTicketsService/getBulkCommentCounts", {
        signature,
        ticket_ids: ticketIds
      });
      const counts = response.data?.message?.[0]?.data || {};
      setCommentCounts(counts);
    } catch (error) {
      console.error("Error fetching comment counts:", error);
    }
  }, [signature]);

  // Fetch employee details
  const fetchEmployeeDetails = useCallback(async () => {
    try {
      const response = await api.post("userService/getEmployeesListData", {
        signature,
      });
      const employeeData = response?.data?.data?.data?.allUsers || [];
      setEmployeeDetail(employeeData);
    } catch (error) {
      console.error("Error fetching employee details:", error);
      showBasicAlert("Failed to load employee details");
    }
  }, [signature]);

  // Fetch companies on component mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [companiesRes] = await Promise.all([
          api.post("userService/getAllCompanyList", { signature }),
          fetchEmployeeDetails()
        ]);

        const companiesData = companiesRes?.data?.data?.data || [];
        setCompanies(companiesData);

        if (companiesData.length > 0) {
          setCompanyId(companiesData[0].id);
          setCompanyType(companiesData[0].type);
        }
      } catch (error) {
        console.error("Error fetching initial data:", error);
        showBasicAlert("Failed to load companies");
      }
    };

    fetchInitialData();
  }, [signature, fetchEmployeeDetails]);

  // Fetch assigned tickets and metrics together
  const fetchTicketData = useCallback(async () => {
    if (!companyId || !companyType) return;

    try {
      setLoading(true);
      const [ticketsRes, metricsRes] = await Promise.all([
        api.post("raiseTicketsService/getAssignedTickets", {
          signature,
          company_id: companyId,
          company_type: companyType,
        }),
        api.post("raiseTicketsService/getAssignedTicketsMetrics", {
          signature,
          company_id: companyId,
          company_type: companyType,
        })
      ]);

      const tickets = ticketsRes?.data?.message?.[0]?.data || [];
      setAssignedTickets(tickets);
      setMetrics(metricsRes?.data?.message?.[0]?.data || {});

      // Fetch comment counts in bulk
      if (tickets.length > 0) {
        const ticketIds = tickets.map((t: TicketRecord) => t.id);
        await fetchBulkCommentCounts(ticketIds);
      }

      // Fetch statistics in background
      api.post("raiseTicketsService/getTicketStatistics", {
        signature,
        company_id: companyId,
        company_type: companyType,
        period: "month",
      }).catch(err => console.error("Error fetching statistics:", err));

    } catch (error: any) {
      console.error("Error fetching ticket data:", error);
      showBasicAlert(error?.response?.data?.message?.[0]?.msg || "Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, [companyId, companyType, signature, fetchBulkCommentCounts]);

  useEffect(() => {
    if (companyId && companyType) {
      fetchTicketData();
    }
  }, [companyId, companyType, fetchTicketData]);

  // Get employee name from cache
  const getEmployeeName = useCallback((employeeId: number) => {
    if (!employeeId) return "Unknown";
    return employeeNameCache[employeeId] || `User #${employeeId}`;
  }, [employeeNameCache]);

  // Get employee avatar from cache
  const getEmployeeAvatar = useCallback((employeeId: number) => {
    if (!employeeId) return <UserOutlined style={{ fontSize: "18px", color: "#6c757d" }} />;

    const imageUrl = employeeImageCache[employeeId];
    const employeeName = employeeNameCache[employeeId];

    if (imageUrl) {
      return (
        <img
          src={imageUrl}
          alt={employeeName || `User ${employeeId}`}
          className="rounded-circle"
          style={{
            width: "24px",
            height: "24px",
            objectFit: "cover",
          }}
        />
      );
    }

    if (employeeName) {
      const names = employeeName.split(" ");
      const firstLetter = names[0]?.[0] || "";
      const lastLetter = names[names.length - 1]?.slice(-1) || "";
      const initials = (firstLetter + lastLetter).toUpperCase();

      return (
        <div
          className="rounded-circle bg-primary text-white d-flex align-items-center justify-content-center"
          style={{
            width: "24px",
            height: "24px",
            fontSize: "11px",
            fontWeight: "bold",
          }}
        >
          {initials}
        </div>
      );
    }

    return <UserOutlined style={{ fontSize: "18px", color: "#6c757d" }} />;
  }, [employeeImageCache, employeeNameCache]);

  // Memoized unique categories
  const uniqueCategories = useMemo(() => {
    return Array.from(new Set(assignedTickets.map((ticket) => ticket.category)));
  }, [assignedTickets]);

  // Memoized filtered tickets
  const filteredTickets = useMemo(() => {
    return assignedTickets.filter((ticket) => {
      const matchesStatus = selectedStatus === "all" || ticket.status === selectedStatus;
      const matchesPriority = selectedPriority === "all" || ticket.priority === selectedPriority;
      const matchesCategory = selectedCategory === "all" || ticket.category === selectedCategory;
      const matchesSearch = searchTerm === "" ||
        ticket.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ticket.description.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesStatus && matchesPriority && matchesCategory && matchesSearch;
    });
  }, [assignedTickets, selectedStatus, selectedPriority, selectedCategory, searchTerm]);

  // Add these functions after fetchComments (around line 250)

  const handleAddComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() || !currentTicketId) return;

    try {
      const tempId = Date.now();
      const newCommentObj = {
        id: tempId,
        comment: newComment,
        createdAt: new Date().toISOString(),
        user_id: parseInt(localStorage.getItem("user_id") || "0"),
        t_ticket_id: currentTicketId,
      };

      setComments((prev) => [...prev, newCommentObj]);
      setCommentCounts((prev) => ({
        ...prev,
        [currentTicketId]: (prev[currentTicketId] || 0) + 1,
      }));
      setNewComment("");

      const res = await api.post("raiseTicketsService/addCommentAndUpdateStatus", {
        t_ticket_id: currentTicketId,
        comment: newComment,
        signature,
      });

      const serverComment = res.data?.message?.[0]?.data;
      if (serverComment && serverComment.id) {
        setComments((prev) =>
          prev.map((comment) =>
            comment.id === tempId
              ? { ...comment, id: serverComment.id }
              : comment
          )
        );
      }

      await fetchComments(currentTicketId);
    } catch (error) {
      console.error("Error adding comment:", error);
      if (currentTicketId) await fetchComments(currentTicketId);
    }
  };

  const handleUpdateComment = async (commentId: number, updatedText: string) => {
    if (!currentTicketId) return;

    try {
      setComments((prev) =>
        prev.map((comment) =>
          comment.id === commentId
            ? { ...comment, comment: updatedText }
            : comment
        )
      );
      setEditingCommentId(null);
      setEditCommentText("");

      await api.post("raiseTicketsService/updateComment", {
        signature,
        id: commentId,
        t_ticket_id: currentTicketId,
        comment: updatedText,
      });

      await fetchComments(currentTicketId);
    } catch (error) {
      console.error("Error updating comment:", error);
      if (currentTicketId) await fetchComments(currentTicketId);
    }
  };

  const handleDeleteComment = (commentId: number) => {
    setDeleteCommentModal({
      visible: true,
      commentId: commentId
    });
  };

  const confirmDeleteComment = async () => {
    if (!deleteCommentModal.commentId || !currentTicketId) {
      setDeleteCommentModal({ visible: false, commentId: null });
      return;
    }

    try {
      const commentId = deleteCommentModal.commentId;

      // Optimistic update
      setComments((prev) => prev.filter((comment) => comment.id !== commentId));
      setCommentCounts((prev) => ({
        ...prev,
        [currentTicketId]: Math.max((prev[currentTicketId] || 0) - 1, 0),
      }));

      // API call
      await api.post("raiseTicketsService/deleteComment", {
        signature,
        id: commentId,
      });

      await fetchComments(currentTicketId);
      showBasicAlert("Comment deleted successfully");
    } catch (error: any) {
      console.error("Error deleting comment:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to delete comment. Please try again.";
      showBasicAlert(errorMessage);
      if (currentTicketId) await fetchComments(currentTicketId);
    } finally {
      setDeleteCommentModal({ visible: false, commentId: null });
    }
  };

  // const handleProgressUpdate = useCallback(async (ticketId: number, newProgress: number) => {
  //   if (!companyId || !companyType) return;

  //   setTicketUpdating((prev) => ({ ...prev, [ticketId]: true }));

  //   try {
  //     await api.post("raiseTicketsService/updateTicketProgress", {
  //       signature,
  //       id: ticketId,
  //       progress: newProgress,
  //       company_id: companyId,
  //       company_type: companyType,
  //     });

  //     setAssignedTickets((prevTickets) =>
  //       prevTickets.map((ticket) =>
  //         ticket.id === ticketId ? { ...ticket, progress: newProgress } : ticket
  //       )
  //     );

  //     // Refresh metrics
  //     fetchTicketData();
  //     showBasicAlert("Progress updated successfully");
  //   } catch (error: any) {
  //     console.error("Error updating progress:", error);
  //     showBasicAlert(error?.response?.data?.message?.[0]?.msg || "Failed to update progress");
  //   } finally {
  //     setTicketUpdating((prev) => ({ ...prev, [ticketId]: false }));
  //   }
  // }, [companyId, companyType, signature, fetchTicketData]);

  // Update ticket status
  const handleStatusUpdate = useCallback(async (ticketId: number, newStatus: string) => {
    if (!companyId || !companyType) return;

    setTicketUpdating((prev) => ({ ...prev, [ticketId]: true }));

    try {
      await api.post("raiseTicketsService/bulkUpdateTicketStatus", {
        signature,
        ticket_ids: [ticketId],
        status: newStatus,
        company_id: companyId,
        company_type: companyType,
      });

      setAssignedTickets((prevTickets) =>
        prevTickets.map((ticket) =>
          ticket.id === ticketId ? { ...ticket, status: newStatus } : ticket
        )
      );

      fetchTicketData();
      showBasicAlert("Status updated successfully");
    } catch (error: any) {
      console.error("Error updating status:", error);
      showBasicAlert(error?.response?.data?.message?.[0]?.msg || "Failed to update status");
    } finally {
      setTicketUpdating((prev) => ({ ...prev, [ticketId]: false }));
    }
  }, [companyId, companyType, signature, fetchTicketData]);

  // Handle company change
  const handleCompanyChange = useCallback((selected: any) => {
    if (selected) {
      const [id, type] = selected.value.split("-");
      setCompanyId(parseInt(id));
      setCompanyType(type);
    }
  }, []);

  // Priority styling - memoized
  const getPriorityStyle = useCallback((priority: string) => {
    const styles = {
      high: { color: "red", label: "High" },
      medium: { color: "orange", label: "Medium" },
      low: { color: "green", label: "Low" },
    };
    return styles[priority as keyof typeof styles] || { color: "default", label: "Normal" };
  }, []);

  // Status styling - memoized
  const getStatusStyle = useCallback((status: string) => {
    const styles = {
      completed: { color: "success", label: "Completed" },
      "in-progress": { color: "processing", label: "In Progress" },
      pending: { color: "warning", label: "Pending" },
    };
    return styles[status as keyof typeof styles] || { color: "default", label: "Unknown" };
  }, []);

  // Handle refresh
  const handleRefresh = useCallback(() => {
    fetchTicketData();
  }, [fetchTicketData]);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    setSearchTerm("");
    setSelectedStatus("all");
    setSelectedPriority("all");
    setSelectedCategory("all");
  }, []);

  // Comments Modal Functions
  const showCommentsModal = useCallback(async (ticketId: number) => {
    setCurrentTicketId(ticketId);
    setIsCommentsModalVisible(true);
    await fetchComments(ticketId);
  }, []);

  const fetchComments = useCallback(async (ticketId: number) => {
    try {
      setCommentsLoading(true);
      const response = await api.post("raiseTicketsService/getCommentsByTicketId", {
        signature,
        t_ticket_id: ticketId,
      });

      const allComments = response.data?.message?.[0]?.data || [];
      setComments(allComments.map((comment: any) => ({
        id: comment.id,
        comment: comment.comment,
        createdAt: comment.createdAt,
        user_id: comment.user_id,
        t_ticket_id: comment.t_ticket_id,
      })));
    } catch (error) {
      console.error("Error fetching comments:", error);
    } finally {
      setCommentsLoading(false);
    }
  }, [signature]);

  // Ant Design Table Columns
  const columns: ColumnsType<TicketRecord> = useMemo(() => [
    {
      title: "Ticket Info",
      key: "ticket_info",
      width: "32%",
      render: (record: TicketRecord) => {
        const raisedByName = getEmployeeName(record.raised_by);
        const raisedByAvatar = getEmployeeAvatar(record.raised_by);

        const titleLimit = 40;
        const descLimit = 50;

        const shortTitle = record.title.length > titleLimit
          ? record.title.substring(0, titleLimit) + "..."
          : record.title;

        const shortDesc = record.description?.length > descLimit
          ? record.description.substring(0, descLimit) + "..."
          : record.description;

        return (
          <div className="d-flex" style={{ gap: "6px" }}>
            <div style={{ minWidth: "115px" }}>
              <div style={{ position: "relative", cursor: "pointer", width: "fit-content" }}>
                <div style={{ transform: "scale(1.25)" }}>{raisedByAvatar}</div>
                <span
                  style={{
                    position: "absolute",
                    top: "-26px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "#000",
                    color: "#fff",
                    borderRadius: "4px",
                    padding: "2px 6px",
                    fontSize: "11px",
                    whiteSpace: "nowrap",
                    opacity: 0,
                    transition: "0.3s",
                  }}
                  className="empNameLabel"
                  onMouseEnter={(e) => (e.currentTarget.style.opacity = "1")}
                  onMouseLeave={(e) => (e.currentTarget.style.opacity = "0")}
                >
                  {raisedByName}
                </span>
              </div>
              <small className="text-muted d-flex align-items-center mt-1" style={{ gap: "4px", fontSize: "11px" }}>
                <CalendarOutlined />
                {moment(record.raised_date).format("MMM DD")}
              </small>
            </div>
            <div>
              <h6 className="mb-1 fw-semibold" style={{ color: "#333" }}>
                {shortTitle}
                {record.title.length > titleLimit && (
                  <span
                    onClick={() => {
                      setFullTextValue(record.title);
                      setOpenFullText(true);
                    }}
                    style={{
                      color: "#007bff",
                      marginLeft: "6px",
                      cursor: "pointer",
                      fontSize: "12px",
                    }}
                  >
                    View
                  </span>
                )}
              </h6>
              <p style={{ fontSize: "0.85rem", marginTop: "-2px" }} className="text-muted mb-0">
                {shortDesc}
                {record.description?.length > descLimit && (
                  <span
                    onClick={() => {
                      setFullTextValue(record.description);
                      setOpenFullText(true);
                    }}
                    style={{
                      color: "#007bff",
                      marginLeft: "6px",
                      cursor: "pointer",
                      fontSize: "12px",
                      textDecoration: "underline",
                    }}
                  >
                    View
                  </span>
                )}
              </p>
            </div>
          </div>
        );
      },
    },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: "10%",
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    {
      title: "Priority",
      dataIndex: "priority",
      key: "priority",
      width: "8%",
      render: (priority: string) => {
        const style = getPriorityStyle(priority);
        return <Tag color={style.color}>{style.label}</Tag>;
      },
      filters: [
        { text: "High", value: "high" },
        { text: "Medium", value: "medium" },
        { text: "Low", value: "low" },
      ],
      onFilter: (value, record) => record.priority === value,
    },
    {
      title: "Progress",
      dataIndex: "progress",
      key: "progress",
      width: "12%",
      render: (progress: number) => (
        <Progress percent={progress} size="small" status={progress === 100 ? "success" : "active"} />
      ),
      sorter: (a, b) => a.progress - b.progress,
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      key: "due_date",
      width: "10%",
      render: (date: string, record: TicketRecord) => {
        const isOverdue = record.is_overdue && record.status !== "completed";
        return (
          <div>
            <span className={isOverdue ? "text-danger" : ""}>
              {moment(date).format("MMM DD, YYYY")}
            </span>
            {isOverdue && (
              <i className="ti ti-alert-triangle text-danger ms-2" title="Overdue"></i>
            )}
          </div>
        );
      },
      sorter: (a, b) => moment(a.due_date).unix() - moment(b.due_date).unix(),
    },
    {
      title: "Comments",
      key: "comments",
      render: (_, record: TicketRecord) => (
        <Button
          type="default"
          size="small"
          icon={<CommentOutlined />}
          onClick={() => showCommentsModal(record.id)}
        >
          {commentCounts[record.id] || 0}
        </Button>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: "10%",
      render: (status: string, record: TicketRecord) => {
        const style = getStatusStyle(status);
        return (
          <Select
            value={status}
            onChange={(newStatus) => handleStatusUpdate(record.id, newStatus)}
            style={{ width: "100%" }}
            disabled={ticketUpdating[record.id]}
            size="small"
          >
            <Option value="in-progress">
              <Tag color="processing">In Progress</Tag>
            </Option>
            <Option value="pending">
              <Tag color="warning">Pending</Tag>
            </Option>
            <Option value="completed">
              <Tag color="success">Completed</Tag>
            </Option>
          </Select>
        );
      },
      filters: [
        { text: "In Progress", value: "in-progress" },
        { text: "Pending", value: "pending" },
        { text: "Completed", value: "completed" },
      ],
      onFilter: (value, record) => record.status === value,
    },
  ], [getEmployeeName, getEmployeeAvatar, getPriorityStyle, getStatusStyle, commentCounts, ticketUpdating, handleStatusUpdate, showCommentsModal]);

  // Show clear button condition
  const showClearButton = useMemo(() => {
    return !!(searchTerm ||
      selectedStatus !== "all" ||
      selectedPriority !== "all" ||
      selectedCategory !== "all");
  }, [searchTerm, selectedStatus, selectedPriority, selectedCategory]);

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
          <div className="my-auto mb-2">
            <h2 className="mb-1">Assigned Tickets</h2>
            <nav>
              <ol className="breadcrumb mb-0">
                <li className="breadcrumb-item">
                  <Link to={routes.adminDashboard}>
                    <i className="ti ti-smart-home" />
                  </Link>
                </li>
                <li className="breadcrumb-item">Tasks</li>
                <li className="breadcrumb-item active" aria-current="page">
                  Assigned Tickets
                </li>
              </ol>
            </nav>
          </div>
          <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
            <div className="dropdown me-2 mb-2">
              <CommonSelect
                className="select"
                options={companies.map((company) => ({
                  value: `${company.id}-${company.type}`,
                  label: `${company.name} - ${company.type}`,
                }))}
                defaultValue={companyId && companyType ? {
                  value: `${companyId}-${companyType}`,
                  label: `${companies.find((c) => c.id === companyId)?.name || ""} - ${companyType}`,
                } : undefined}
                onChange={handleCompanyChange}
                isDisabled={loading}
              />
            </div>
            <Button
              className="mb-2 me-2"
              icon={<i className="ti ti-refresh"></i>}
              onClick={handleRefresh}
              disabled={loading || !companyId}
            >
              Refresh
            </Button>
            <div className="head-icons ms-2">
              <CollapseHeader />
            </div>
          </div>
        </div>

        {/* Metrics Dashboard */}
        <div className="row">
          <div className="col-xl-3 col-md-6">
            <div className="card" style={{ border: "none", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="bg-primary d-flex align-items-center justify-content-center text-white rounded"
                    style={{ width: "50px", height: "80px", borderRadius: "10px", fontSize: "1.5rem" }}>
                    <i className="ti ti-checklist"></i>
                  </div>
                  <div className="ms-3">
                    <h4 className="mb-0">{metrics.totalTickets || 0}</h4>
                    <p className="text-muted mb-0">Total Assigned</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-md-6">
            <div className="card" style={{ border: "none", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="bg-success d-flex align-items-center justify-content-center text-white rounded"
                    style={{ width: "50px", height: "80px", borderRadius: "10px", fontSize: "1.5rem" }}>
                    <i className="ti ti-circle-check"></i>
                  </div>
                  <div className="ms-3">
                    <h4 className="mb-0">{metrics.completedTickets || 0}</h4>
                    <p className="text-muted mb-0">Completed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-xl-3 col-md-6">
            <div className="card" style={{ border: "none", boxShadow: "0 2px 4px rgba(0,0,0,0.1)" }}>
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="bg-warning d-flex align-items-center justify-content-center text-white rounded"
                    style={{ width: "50px", height: "80px", borderRadius: "10px", fontSize: "1.5rem" }}>
                    <i className="ti ti-clock"></i>
                  </div>
                  <div className="ms-3">
                    <h4 className="mb-0">{(metrics.inProgressTickets || 0) + (metrics.pendingTickets || 0)}</h4>
                    <p className="text-muted mb-0">Active Tickets</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tickets List */}
        <div className="card">
          <div className="card-header">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
              <h5 className="card-title mb-0">Assigned Tickets</h5>
              <div className="d-flex gap-2 flex-wrap">
                <Select
                  placeholder="Status"
                  value={selectedStatus}
                  onChange={setSelectedStatus}
                  style={{ width: 140 }}
                  disabled={loading || !companyId}
                  options={STATUS_OPTIONS}
                />
                <Select
                  placeholder="Priority"
                  value={selectedPriority}
                  onChange={setSelectedPriority}
                  style={{ width: 140 }}
                  disabled={loading || !companyId}
                  options={PRIORITY_OPTIONS}
                />
                <Select
                  placeholder="Category"
                  value={selectedCategory}
                  onChange={setSelectedCategory}
                  style={{ width: 150 }}
                  disabled={loading || !companyId}
                >
                  <Option value="all">All Categories</Option>
                  {uniqueCategories.map((category) => (
                    <Option key={category} value={category}>{category}</Option>
                  ))}
                </Select>
                {showClearButton && (
                  <Button onClick={handleClearFilters}>Clear Filters</Button>
                )}
              </div>
            </div>
          </div>
          <div className="card-body p-0">
            {!companyId ? (
              <div className="text-center py-5">
                <i className="ti ti-building fs-1 text-muted"></i>
                <p className="mt-3 text-muted">Please select a company to view tickets</p>
              </div>
            ) : (
              <Datatable columns={columns} dataSource={filteredTickets} />
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
        <p className="mb-0">2025 © CMS.</p>
        <p>Designed & Developed By <Link to="#" className="text-primary">ZYVO</Link></p>
      </div>

      {/* Comments Modal - Keep as is but use employeeImageCache */}
      <Modal
        title={
          <div className="d-flex align-items-center justify-content-between">
            <div className="d-flex align-items-center">
              <div className="d-flex align-items-center justify-content-center me-3"
                style={{ width: "40px", height: "40px", borderRadius: "10px", backgroundColor: "#e6f7ff" }}>
                <CommentOutlined style={{ fontSize: "20px", color: "#1890ff" }} />
              </div>
              <div>
                <h5 className="mb-0">Discussion</h5>
                <small className="text-muted">
                  {commentCounts[currentTicketId || 0] || 0}{" "}
                  {commentCounts[currentTicketId || 0] === 1 ? "comment" : "comments"}
                </small>
              </div>
            </div>
          </div>
        }
        open={isCommentsModalVisible}
        onCancel={() => {
          setIsCommentsModalVisible(false);
          setCurrentTicketId(null);
          setNewComment("");
          setEditingCommentId(null);
          setEditCommentText("");
        }}
        footer={null}
        width={750}
        style={{ top: 20 }}
      >
        {/* Comment modal content - update to use employeeImageCache */}
        <div style={{ display: "flex", flexDirection: "column", height: "70vh" }}>
          {/* Add Comment Form */}
          <div className="p-3 mb-3" style={{ backgroundColor: "#fafafa", borderRadius: "12px", border: "1px solid #f0f0f0" }}>
            <form onSubmit={handleAddComment}>
              <div className="d-flex align-items-start gap-3">
                <div style={{ flexShrink: 0 }}>
                  {user?.userDetails?.user_id ? (
                    (() => {
                      const currentUserImageUrl = employeeImageCache[parseInt(user.userDetails.user_id)];
                      return currentUserImageUrl ? (
                        <Avatar size={40} src={currentUserImageUrl} />
                      ) : (
                        <Avatar size={40} style={{ backgroundColor: "#1890ff", fontSize: "16px", fontWeight: "600" }}>
                          {user?.userDetails?.name?.charAt(0)?.toUpperCase() || "U"}
                        </Avatar>
                      );
                    })()
                  ) : (
                    <Avatar size={40} icon={<UserOutlined />} style={{ backgroundColor: "#1890ff" }} />
                  )}
                </div>
                <div style={{ flex: 1 }}>
                  <TextArea
                    rows={3}
                    placeholder="Share your thoughts..."
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    style={{ borderRadius: "8px", resize: "none", fontSize: "14px" }}
                    className="mb-2"
                  />
                  <div className="d-flex justify-content-end">
                    <Button
                      type="primary"
                      htmlType="submit"
                      icon={<SendOutlined />}
                      disabled={!newComment.trim()}
                      style={{ borderRadius: "6px", height: "36px", paddingLeft: "20px", paddingRight: "20px" }}
                    >
                      Post Comment
                    </Button>
                  </div>
                </div>
              </div>
            </form>
          </div>

          {/* Comments List */}
          <div style={{ flex: 1, overflowY: "auto", paddingRight: "8px" }}>
            {commentsLoading ? (
              <div className="d-flex flex-column align-items-center justify-content-center" style={{ height: "300px" }}>
                <div className="spinner-border text-primary mb-3" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted">Loading comments...</p>
              </div>
            ) : comments.length === 0 ? (
              <div className="d-flex flex-column align-items-center justify-content-center text-muted" style={{ height: "300px" }}>
                <div className="d-flex align-items-center justify-content-center mb-3"
                  style={{ width: "80px", height: "80px", borderRadius: "50%", backgroundColor: "#f5f5f5" }}>
                  <CommentOutlined style={{ fontSize: "32px", color: "#bfbfbf" }} />
                </div>
                <h6 className="mb-2">No comments yet</h6>
                <p className="text-center" style={{ fontSize: "14px" }}>Be the first to share your thoughts!</p>
              </div>
            ) : (
              <div className="d-flex flex-column gap-3">
                {[...comments]
                  .filter((c) => c.t_ticket_id === currentTicketId)
                  .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                  .map((comment, index) => {
                    const employeeName = employeeNameCache[comment.user_id] || "Managing Director";
                    const imageUrl = employeeImageCache[comment.user_id];
                    const isEditing = editingCommentId === comment.id;
                    const isCurrentUser = parseInt(user?.userDetails?.id) === comment.user_id;

                    return (
                      <div key={comment.id} style={{
                        padding: "16px",
                        backgroundColor: index === 0 ? "#f6ffed" : "#fff",
                        borderRadius: "12px",
                        border: `1px solid ${index === 0 ? "#b7eb8f" : "#f0f0f0"}`,
                      }}>
                        <div className="d-flex align-items-start gap-3">
                          <div style={{ flexShrink: 0 }}>
                            {imageUrl ? (
                              <Avatar size={42} src={imageUrl} />
                            ) : (
                              <Avatar size={42} style={{
                                backgroundColor: isCurrentUser ? "#1890ff" : "#52c41a",
                                fontSize: "16px",
                                fontWeight: "600",
                              }}>
                                {employeeName.charAt(0).toUpperCase()}
                              </Avatar>
                            )}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="d-flex justify-content-between align-items-start mb-2">
                              <div>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <h6 className="mb-0" style={{ fontSize: "15px", fontWeight: "600", color: "#262626" }}>
                                    {employeeName}
                                  </h6>
                                  {isCurrentUser && (
                                    <span style={{
                                      fontSize: "11px",
                                      padding: "2px 8px",
                                      backgroundColor: "#e6f7ff",
                                      color: "#1890ff",
                                      borderRadius: "4px",
                                      fontWeight: "500",
                                    }}>
                                      You
                                    </span>
                                  )}
                                  {index === 0 && (
                                    <span style={{
                                      fontSize: "11px",
                                      padding: "2px 8px",
                                      backgroundColor: "#f6ffed",
                                      color: "#52c41a",
                                      borderRadius: "4px",
                                      fontWeight: "500",
                                    }}>
                                      Latest
                                    </span>
                                  )}
                                </div>
                                <small className="text-muted" style={{ fontSize: "12px" }}>
                                  <CalendarOutlined style={{ marginRight: "4px", fontSize: "12px" }} />
                                  {moment(comment.createdAt).fromNow()}
                                </small>
                              </div>
                              {isCurrentUser && (
                                <div className="d-flex align-items-center gap-1">
                                  <Button
                                    type="text"
                                    size="small"
                                    icon={<EditOutlined style={{ fontSize: "14px" }} />}
                                    onClick={() => {
                                      setEditingCommentId(comment.id);
                                      setEditCommentText(comment.comment);
                                    }}
                                    style={{ color: "#8c8c8c", height: "28px", width: "28px" }}
                                    title="Edit comment"
                                  />
                                  <Button
                                    type="text"
                                    size="small"
                                    data-bs-toggle="modal"
                                    data-bs-target="#delete_comment_modal"
                                    danger
                                    icon={<DeleteOutlined style={{ fontSize: "14px" }} />}
                                    onClick={() => handleDeleteComment(comment.id)}
                                    style={{ height: "28px", width: "28px" }}
                                    title="Delete comment"
                                  />
                                </div>
                              )}
                            </div>
                            {isEditing ? (
                              <div>
                                <TextArea
                                  rows={3}
                                  value={editCommentText}
                                  onChange={(e) => setEditCommentText(e.target.value)}
                                  style={{ borderRadius: "8px", resize: "none", fontSize: "14px" }}
                                  className="mb-2"
                                  autoFocus
                                />
                                <Space>
                                  <Button
                                    type="primary"
                                    size="small"
                                    onClick={() => handleUpdateComment(comment.id, editCommentText)}
                                    disabled={!editCommentText.trim()}
                                    style={{ borderRadius: "6px", height: "32px" }}
                                  >
                                    Save
                                  </Button>
                                  <Button
                                    size="small"
                                    onClick={() => {
                                      setEditingCommentId(null);
                                      setEditCommentText("");
                                    }}
                                    style={{ borderRadius: "6px", height: "32px" }}
                                  >
                                    Cancel
                                  </Button>
                                </Space>
                              </div>
                            ) : (
                              <p className="mb-0" style={{
                                fontSize: "14px",
                                lineHeight: "1.6",
                                color: "#595959",
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                              }}>
                                {comment.comment}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      </Modal>

      <DeleteModal
        modalId="delete_comment_modal"
        onDelete={confirmDeleteComment}
        title="Delete Comment"
        message="Are you sure you want to delete this comment? This action cannot be undone."
      />

      <Modal
        title="Ticket Title"
        open={openFullText}
        footer={null}
        onCancel={() => setOpenFullText(false)}
      >
        <p style={{ fontSize: "15px", color: "#333" }}>{fullTextValue}</p>
      </Modal>
    </div>
  );
};

export default AssignedTickets;